import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '../services/apiService';

interface UseInactivityTimeoutOptions {
  timeoutMinutes?: number;    // Hard sign-out after this much inactivity (default: 120)
  warnAfterMinutes?: number;  // Show the "still there?" warning at this point (default: 30)
  onTimeout?: () => void;     // Callback when timeout occurs
  enabled?: boolean;          // Whether to enable the timeout (default: true when logged in)
}

interface UseInactivityTimeoutReturn {
  showInactivityPopup: boolean;
  remainingSeconds: number;
  handleStayLoggedIn: () => void;
  handleLogout: () => void;
}

const INACTIVITY_TIMEOUT_MINUTES = 120; // sign out after 2 hours of inactivity
const WARN_AFTER_MINUTES = 30;          // warn after 30 minutes of inactivity
// Below this much time left, the countdown ticks every second because seconds
// have started to matter. Above it, ticking once a second would re-render the
// whole app ~5,400 times across the gap between warning and sign-out.
const FINE_COUNTDOWN_SECONDS = 5 * 60;

export const useInactivityTimeout = (
  options: UseInactivityTimeoutOptions = {}
): UseInactivityTimeoutReturn => {
  const {
    timeoutMinutes = INACTIVITY_TIMEOUT_MINUTES,
    warnAfterMinutes = WARN_AFTER_MINUTES,
    onTimeout,
    enabled = true,
  } = options;

  const [showInactivityPopup, setShowInactivityPopup] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  // Absolute moment the session dies. The countdown reads from this rather
  // than decrementing, so a throttled background tab cannot drift it.
  const deadlineRef = useRef<number>(0);
  // Bumping this re-runs the main effect, which re-arms the timers. Needed
  // after "Stay signed in": otherwise nothing is armed until the user happens
  // to move the mouse, and a motionless user is never warned or signed out.
  const [rearmNonce, setRearmNonce] = useState(0);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Convert minutes to milliseconds
  const timeoutMs = timeoutMinutes * 60 * 1000;
  // Set directly now. It used to be `(timeoutMinutes - warningBeforeMinutes)`,
  // which meant you could not move the warning without moving the sign-out.
  const warningMs = Math.min(warnAfterMinutes * 60 * 1000, timeoutMs);

  // Track popup state in a ref so the activity handler can read it without re-binding listeners
  const showPopupRef = useRef(false);
  useEffect(() => {
    showPopupRef.current = showInactivityPopup;
  }, [showInactivityPopup]);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    if (!enabled) return;
    
    // Do NOT update activity or dismiss popup when the inactivity warning is showing.
    // The user must explicitly click "Stay Logged In" or "Logout".
    if (showPopupRef.current) return;
    
    const now = Date.now();
    lastActivityRef.current = now;
    apiService.updateLastActivity();
  }, [enabled]);

  // Handle stay logged in
  const handleStayLoggedIn = useCallback(() => {
    setShowInactivityPopup(false);
    setRemainingSeconds(0);
    // updateActivity() bails while the popup is up, so stamp the activity
    // ourselves after clearing it, then force the timers to re-arm.
    lastActivityRef.current = Date.now();
    apiService.updateLastActivity();
    setRearmNonce(n => n + 1);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    setShowInactivityPopup(false);
    if (onTimeout) {
      onTimeout();
    }
  }, [onTimeout]);

  // Start countdown when warning popup is shown
  useEffect(() => {
    if (!showInactivityPopup || !enabled) return;
    let handle: ReturnType<typeof setTimeout> | null = null;
    const tick = () => {
      const left = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000));
      setRemainingSeconds(left);
      if (left <= 0) { handleLogout(); return; }
      // Once a second only when it reads as seconds; coarser before that.
      handle = setTimeout(tick, left <= FINE_COUNTDOWN_SECONDS ? 1000 : 15000);
    };
    tick();
    return () => { if (handle) clearTimeout(handle); };
  }, [showInactivityPopup, enabled, handleLogout]);

  // Main inactivity detection effect
  useEffect(() => {
    if (!enabled) {
      // Clear all timers when disabled
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      return;
    }

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    // Debounced activity handler
    let activityTimeout: NodeJS.Timeout | null = null;
    const handleActivity = () => {
      // Don't process activity when the inactivity popup is showing
      if (showPopupRef.current) return;
      
      if (activityTimeout) return; // Debounce
      
      activityTimeout = setTimeout(() => {
        activityTimeout = null;
      }, 1000); // Only update once per second max
      
      updateActivity();
      resetTimers();
    };

    // Reset all timers
    const resetTimers = () => {
      // Clear existing timers
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);

      // Set warning timer (shows popup before actual timeout)
      warningTimeoutRef.current = setTimeout(() => {
        deadlineRef.current = Date.now() + (timeoutMs - warningMs);
        setShowInactivityPopup(true);
      }, warningMs);
    };

    // Check for existing inactivity on mount
    const checkExistingInactivity = () => {
      const lastActivity = apiService.getLastActivity();
      const now = Date.now();
      const inactiveTime = now - lastActivity;

      // This runs on mount AND on every return to the tab, so it must leave
      // exactly one pending warning behind. It previously assigned a fresh
      // setTimeout without clearing the last one, so every tab switch planted
      // another uncancellable warning — which is why the popup appeared over
      // and over, seemingly on minimise, no matter how active the user was.
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

      if (inactiveTime >= timeoutMs) {
        // Already timed out
        handleLogout();
      } else if (inactiveTime >= warningMs) {
        // Show warning immediately
        deadlineRef.current = lastActivity + timeoutMs;
        setRemainingSeconds(Math.floor((timeoutMs - inactiveTime) / 1000));
        setShowInactivityPopup(true);
      } else {
        // Genuinely active: make sure a warning left over from a previous
        // visit is taken down rather than left covering the page.
        setShowInactivityPopup(false);
        // Calculate remaining time until warning
        const timeUntilWarning = warningMs - inactiveTime;
        warningTimeoutRef.current = setTimeout(() => {
          deadlineRef.current = Date.now() + (timeoutMs - warningMs);
          setShowInactivityPopup(true);
        }, timeUntilWarning);
      }
    };

    // Initialize
    checkExistingInactivity();

    // Add event listeners
    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Also listen for visibility change (user switches back to tab)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkExistingInactivity();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, [enabled, timeoutMs, warningMs, rearmNonce, updateActivity, handleLogout]);

  return {
    showInactivityPopup,
    remainingSeconds,
    handleStayLoggedIn,
    handleLogout,
  };
};

export default useInactivityTimeout;

